import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  effectiveTheme: "dark" | "light";
  /** True once the user (or a first-time server sync) has explicitly chosen a
   *  theme. When true, UserPreferencesSync will never override the local value
   *  with a server-fetched one — the server becomes write-only for this device. */
  themeIsExplicit: boolean;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  effectiveTheme: "light",
  themeIsExplicit: false,
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Point every `theme-color` meta at the page background the app is showing.
 *
 * index.html ships three static metas keyed on prefers-color-scheme, which is
 * the OS's theme, not the app's: a user on a light phone who picks Dark in
 * the account menu got a cream status bar and toolbar over a near-black app.
 * The colour is read back from --background so a company theme is honoured
 * too, and resolved through a computed style so the meta always receives a
 * form every WebKit accepts.
 */
function syncThemeColor() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--background").trim();
  if (!raw) return;
  const probe = document.createElement("span");
  probe.style.color = `hsl(${raw})`;
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  if (!resolved || resolved === "rgba(0, 0, 0, 0)") return;
  const metas = document.querySelectorAll('meta[name="theme-color"]');
  if (metas.length === 0) {
    const meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
    meta.content = resolved;
    return;
  }
  metas.forEach((meta) => meta.setAttribute("content", resolved));
}

/* Storage can THROW, not just return null: Safari in a private window with
   storage blocked, a sandboxed frame, a quota error. Reading the theme was the
   first thing the app did, unguarded, so any of those took the whole app down
   before it drew (K24). A failed read is "no preference"; a failed write
   keeps the choice for this visit. */
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* kept in memory for this visit */
  }
}
function readTheme(key: string): Theme | null {
  const v = readStorage(key);
  return v === "dark" || v === "light" || v === "system" ? v : null;
}

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  ...props
}: ThemeProviderProps) {
  const explicitKey = `${storageKey}-explicit`;

  const [theme, setTheme] = useState<Theme>(
    () => readTheme(storageKey) ?? defaultTheme
  );

  // Persisted across sessions: once the user has ever made an explicit choice,
  // this stays true so server syncs can never silently override it.
  const [themeIsExplicit, setThemeIsExplicit] = useState<boolean>(
    () => readStorage(explicitKey) === "true"
  );

  /* Every open tab follows a change made in any of them (K24). The `storage`
     event fires in the OTHER tabs of this origin when one writes, so a reader
     who switches to Dark in one window does not come back to a second window
     still in Light — and, for an installed app beside a browser tab, to two
     different apps. */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) setTheme(readTheme(storageKey) ?? defaultTheme);
      if (e.key === explicitKey) setThemeIsExplicit(e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, explicitKey, defaultTheme]);

  const [effectiveTheme, setEffectiveTheme] = useState<"dark" | "light">(() => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return theme;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove("light", "dark");
    // After the class lands, in either branch below.
    const tint = () => queueMicrotask(syncThemeColor);

    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

      const applySystemTheme = (e?: MediaQueryListEvent) => {
        const isDark = e !== undefined ? e.matches : mediaQuery.matches;
        const systemTheme = isDark ? "dark" : "light";
        root.classList.remove("light", "dark");
        root.classList.add(systemTheme);
        setEffectiveTheme(systemTheme);
        tint();
      };

      applySystemTheme();
      mediaQuery.addEventListener("change", applySystemTheme);
      return () => mediaQuery.removeEventListener("change", applySystemTheme);
    }

    root.classList.add(theme);
    setEffectiveTheme(theme);
    tint();
  }, [theme]);

  const value: ThemeProviderState = {
    theme,
    effectiveTheme,
    themeIsExplicit,
    setTheme: (newTheme: Theme) => {
      writeStorage(storageKey, newTheme);
      writeStorage(explicitKey, "true");
      setTheme(newTheme);
      setThemeIsExplicit(true);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
